// ./src/store/index.ts

import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import userReducer, { setLoading } from './slices/userSlice';
import invitationReducer, { setIsSaveEnabled, selectProjectChanges } from './slices/invitationSlice';
import designReducer from './slices/designSlice';
import bitmapReducer from './slices/bitmapSlice';
import contactsReducer from './slices/contactSlice';
import navigationReducer from './slices/navigationSlice';
import historyReducer from './slices/historySlice';
import { useDispatch } from 'react-redux';
import { ThunkAction, Action } from '@reduxjs/toolkit';

// Noop storage for non-browser environments (like SSR)
const noopStorage = {
  getItem: () => Promise.resolve(null),
  setItem: () => Promise.resolve(),
  removeItem: () => Promise.resolve(),
};

// Check for browser environment
const isBrowser = typeof window !== 'undefined';

// ============================================================================
// GLOBAL EDITOR REGISTRY
// Allows synchronous access to CKEditor instances for committing pending text
// changes before save/navigation, without relying on React's async useEffect.
// ============================================================================
interface EditorRegistryEntry {
  editor: any; // CKEditor instance
  textblockId: string;
  getActivationContent: () => string | null; // Content when editor was activated
}

const editorRegistry = new Map<string, EditorRegistryEntry>();

/**
 * Register a CKEditor instance for a textblock.
 * Called from TextBlockEditor when the editor is ready.
 */
export const registerEditor = (textblockId: string, editor: any, getActivationContent: () => string | null): void => {
  editorRegistry.set(textblockId, { editor, textblockId, getActivationContent });
};

/**
 * Unregister a CKEditor instance.
 * Called from TextBlockEditor on unmount.
 */
export const unregisterEditor = (textblockId: string): void => {
  editorRegistry.delete(textblockId);
};

/**
 * Get all registered editors.
 */
export const getRegisteredEditors = (): Map<string, EditorRegistryEntry> => {
  return editorRegistry;
};

/**
 * Synchronously commit all pending text changes from registered editors to Redux.
 * Returns true if any changes were committed.
 * This should be called BEFORE checking isSaveEnabled/navigating to ensure all text is saved.
 */
export const commitAllPendingEditorChanges = (): boolean => {
  if (!isBrowser || editorRegistry.size === 0) {
    return false;
  }
  
  const state = store.getState() as RootState;
  const currentTextblocks = state.invitation?.invitationProjectState?.design?.designTextblocks || [];
  
  let anyChanges = false;
  const updatedTextblocks = [...currentTextblocks];
  
  editorRegistry.forEach((entry, textblockId) => {
    try {
      const currentText = entry.editor?.getData?.();
      const activationContent = entry.getActivationContent();
      
      if (currentText !== undefined && activationContent !== null && currentText !== activationContent) {
        const index = updatedTextblocks.findIndex((tb: any) => tb.textblockId === textblockId);
        if (index !== -1 && updatedTextblocks[index].text !== currentText) {
          updatedTextblocks[index] = { ...updatedTextblocks[index], text: currentText };
          anyChanges = true;
        }
      }
    } catch (err) {
      console.warn('Failed to commit editor changes for block:', textblockId, err);
    }
  });
  
  if (anyChanges) {
    const { updateProjectDesignState } = require('./slices/invitationSlice');
    store.dispatch(updateProjectDesignState({ designTextblocks: updatedTextblocks }));
  }
  
  return anyChanges;
};

// Transform to exclude transient state from persistence
// isSaveEnabled should always start as false on page load - it's a UI state, not data
const invitationTransform = createTransform(
  // Transform state on its way to being serialized and persisted
  (inboundState: any) => {
    const { isSaveEnabled, ...rest } = inboundState;
    return rest; // Don't persist isSaveEnabled
  },
  // Transform state being rehydrated
  (outboundState: any) => {
    return {
      ...outboundState,
      isSaveEnabled: false, // Always start with save disabled
    };
  },
  { whitelist: ['invitation'] } // Only apply to invitation slice
);

// Persist configuration
const persistConfig = {
  key: 'root',
  storage: isBrowser ? storage : noopStorage,
  whitelist: ['user', 'invitation', 'design', 'bitmap', 'contacts'], // Only persist these reducers
  transforms: [invitationTransform], // Apply transform to filter out transient state
};

// Combine the reducers first
const rootReducer = combineReducers({
  user: userReducer,
  invitation: invitationReducer,
  design: designReducer,
  bitmap: bitmapReducer,
  contacts: contactsReducer,
  navigation: navigationReducer,
  history: historyReducer,
});

// Apply persistReducer to rootReducer in browser environment
const persistedReducer = isBrowser ? persistReducer(persistConfig, rootReducer) : rootReducer;

// Configure the store
// @ts-ignore
export const store = configureStore({
  reducer: persistedReducer as any, // <-- Explicitly cast reducer to bypass type conflicts
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }) as any, // <-- Explicitly cast middleware to bypass type conflicts
});

// Only persist store in the browser
export const persistor = isBrowser
  ? persistStore(store, null, () => {
      if (store.dispatch) {
        store.dispatch(setLoading(false)); // Ensure store.dispatch is defined
        // Note: Change detection for isSaveEnabled is handled in useRehydrationChangeDetection hook
      }
    })
  : null;

/**
 * Run change detection after rehydration.
 * This is called from a useEffect in InvitationLayout to detect unsaved changes after page refresh.
 * Returns true if changes were detected and isSaveEnabled was updated.
 */
export const runRehydrationChangeDetection = (): boolean => {
  if (!isBrowser) return false;
  
  const state = store.getState() as RootState;
  const isSaveEnabled = state.invitation?.isSaveEnabled;
  
  // Only check if save is currently disabled (to avoid unnecessary recalculation)
  if (!isSaveEnabled) {
    const changes = selectProjectChanges(state);
    const hasChanges = changes && Object.keys(changes).length > 0;
    if (hasChanges) {
      store.dispatch(setIsSaveEnabled(true));
      return true;
    }
  }
  return false;
};

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch & {
  <ReturnType = void>(asyncAction: ThunkAction<ReturnType, RootState, unknown, Action<string>>): ReturnType;
};
export const useAppDispatch = () => useDispatch<AppDispatch>();
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
